import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { REPLAY_SPEED_OPTIONS, replaySpeedIndex } from '../utils/replaySpeeds'

/** The slice of ReplayController the key layer drives. */
export interface ReplayHotkeyController {
  getState(): { playing: boolean; speed: number }
  play(): void
  pause(): void
  step(): Promise<boolean>
  exit(): void
  setSpeed(barsPerSec: number): void
}

/**
 * Video-player key layer while bar replay is active on the hovered chart:
 *
 *  - **Space** toggles play/pause (the host's maximize handler yields Space to
 *    a `[data-replay-active]` chart; Option+Enter still maximizes it).
 *  - **ArrowRight** steps one bar. `step()` is async (it can fetch), so an
 *    in-flight guard drops presses while one is pending instead of queueing a
 *    burst.
 *  - **ArrowUp / ArrowDown** cycle the playback speed through the strip's
 *    options.
 *  - **Escape** exits replay, but only as the LAST link in the Escape chain:
 *    it listens on window (bubble reaches window after every document
 *    handler) and yields when the key was already consumed (drawing cancel,
 *    search close, maximize restore, or the capture-phase pause-while-playing
 *    handler). Exit restores the pre-replay anchor, so it is non-destructive.
 *
 * Hover-gated like the chart's other key layers (pointerenter/leave on the
 * chart container, never focus - focusing the container wedges klinecharts'
 * own pointer handling) and inert while typing in a field.
 */
export function useReplayHotkeys(
  elementRef: RefObject<HTMLElement | null>,
  getController: () => ReplayHotkeyController | null,
  active: boolean,
): void {
  const stepInFlight = useRef(false)

  useEffect(() => {
    if (!active) return
    const el = elementRef.current
    if (!el) return

    let hovered = false
    const onEnter = () => {
      hovered = true
    }
    const onLeave = () => {
      hovered = false
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (!hovered) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const t = e.target instanceof HTMLElement ? e.target : null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return
      }
      const controller = getController()
      if (!controller) return

      if (e.code === 'Space') {
        // Space activates a focused button on keyup; one press, one action.
        if (t?.closest?.('button, a, [role="button"]')) return
        e.preventDefault()
        if (controller.getState().playing) controller.pause()
        else controller.play()
        return
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault()
        if (stepInFlight.current) return
        stepInFlight.current = true
        void controller
          .step()
          .catch(() => undefined)
          .finally(() => {
            stepInFlight.current = false
          })
        return
      }
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        e.preventDefault()
        const idx = replaySpeedIndex(controller.getState().speed)
        const next =
          e.code === 'ArrowUp'
            ? Math.min(idx + 1, REPLAY_SPEED_OPTIONS.length - 1)
            : Math.max(idx - 1, 0)
        controller.setSpeed(REPLAY_SPEED_OPTIONS[next])
        return
      }
      if (e.key === 'Escape') {
        // Last link in the chain: anything that consumed the key wins, and a
        // PLAYING replay is paused by the capture-phase handler (which
        // consumes) before this ever fires.
        if (e.defaultPrevented) return
        e.preventDefault()
        controller.exit()
      }
    }

    el.addEventListener('pointerenter', onEnter)
    el.addEventListener('pointerleave', onLeave)
    // Window, not document: bubble order guarantees every document-level
    // consumer (maximize restore etc.) runs first and can claim the key.
    window.addEventListener('keydown', onKeyDown)
    return () => {
      el.removeEventListener('pointerenter', onEnter)
      el.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [elementRef, getController, active])
}
